import { Box, Button, CircularProgress } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import getTheme from "../../theme/theme";
import { PinnacleThemes } from "../../theme/types";
import { resetPassword } from "../../api/services/auth";
import { handleApiError } from "../../utils/errorHandler";
import { BTN_BACK_TO_LOGIN, E_CONFIRM_PASSWORD, E_ENTER_PASSWORD, E_PASSWORD_NOT_MATCHED, RESET_PASSWORD } from "../../utils/constants";
import PasswordAdornment from "../../components/common/PasswordAdornment/PasswordAdornment";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { blue, grey } from "@mui/material/colors";
import PinnacleFormContainer from "../../components/PinnacleFormContainer/PinnacleFormContainer";

export default function ResetPasswordPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const expireToken = searchParams.get("expire");

    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [confirmPasswordTouched, setConfirmPasswordTouched] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const isPasswordValid = password.length > 0;
    const isConfirmPasswordValid = confirmPassword.length > 0;

    const handleTogglePassword = () => setShowPassword((prev) => !prev);
    const handleToggleConfirmPassword = () => setShowConfirmPassword((prev) => !prev);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (!passwordTouched) setPasswordTouched(true);
        if (errorMessage) setErrorMessage(null);
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
        if (!confirmPasswordTouched) setConfirmPasswordTouched(true);
        if (errorMessage) setErrorMessage(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordTouched(true);

        if (!isPasswordValid || !isConfirmPasswordValid) {
            return;
        }

        setFormDisabled(true);
        try {
            const resetPasswordResponse = await resetPassword(password, expireToken);
            pushNotification({
                details: resetPasswordResponse.message,
                type: NotificationTypes.Success,
            });
            navigate("/login");
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            pushNotification({
                details: errorMessage,
                type: NotificationTypes.Error,
            });
        } finally {
            setFormDisabled(false);
        }
    };

    return (
        <PinnacleFormContainer formName={RESET_PASSWORD}>
            <form onSubmit={handleSubmit} noValidate>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* NEW PASSWORD */}
                    <PinnacleTextField
                        error={passwordTouched && (!isPasswordValid || !!errorMessage)}
                        color={passwordTouched && (!isPasswordValid || !!errorMessage) ? 'error' : 'primary'}
                        helperText={
                            passwordTouched && !isPasswordValid
                                ? E_ENTER_PASSWORD
                                : errorMessage
                        }
                        id="newPassword"
                        label="New Password"
                        variant="outlined"
                        onChange={handlePasswordChange}
                        onBlur={() => setPasswordTouched(true)}
                        value={password}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        disabled={formDisabled}
                        slotProps={{
                            input: {
                                endAdornment: <PasswordAdornment show={showPassword} toggleShow={handleTogglePassword} />,
                            },
                        }}
                    />

                    {/* CONFIRM PASSWORD */}
                    <PinnacleTextField
                        error={
                            (confirmPasswordTouched && (!isConfirmPasswordValid || !!errorMessage)) ||
                            password !== confirmPassword
                        }
                        color={
                            (confirmPasswordTouched && (!isConfirmPasswordValid || !!errorMessage)) ||
                                password !== confirmPassword
                                ? 'error'
                                : 'primary'
                        }
                        helperText={
                            confirmPasswordTouched && !isConfirmPasswordValid
                                ? E_CONFIRM_PASSWORD
                                : password !== confirmPassword
                                    ? E_PASSWORD_NOT_MATCHED
                                    : errorMessage
                        }
                        id="confirmNewPassword"
                        label="Confirm New Password"
                        variant="outlined"
                        onChange={handleConfirmPasswordChange}
                        onBlur={() => setConfirmPasswordTouched(true)}
                        value={confirmPassword}
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="current-confirm-password"
                        disabled={formDisabled}
                        slotProps={{
                            input: {
                                endAdornment: <PasswordAdornment show={showConfirmPassword} toggleShow={handleToggleConfirmPassword} />,
                            },
                        }}
                    />
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={!isPasswordValid || formDisabled}
                        sx={{
                            mb: 1.5,
                            '&.Mui-disabled': {
                                backgroundColor: grey[800],
                                color: grey[400],
                                opacity: 1,
                            },
                        }}
                    >
                        {formDisabled ? (
                            <CircularProgress size={24} sx={{ color: blue[300] }} />
                        ) : (
                            "SUBMIT"
                        )}
                    </Button>
                    <Button
                        fullWidth
                        variant="text"
                        sx={{
                            borderColor: theme.palette.common.white,
                            color: theme.palette.common.white,
                        }}
                        onClick={() => navigate("/login")}
                    >
                        {BTN_BACK_TO_LOGIN}
                    </Button>
                </Box>
            </form>
        </PinnacleFormContainer>
    );
}